import * as React from 'react';
import * as ReactDOM from 'react-dom/client';
import {
  AfterViewInit,
  Component,
  ElementRef,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import { CheckoutRoot } from './react/CheckoutRoot';
import { ApiService } from '@app/core/services/api/api.service';
import { ShopService } from '@app/services/shops/shop.service';
import { Router } from '@angular/router';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { Store } from '@ngrx/store';
import { MatDialog } from '@angular/material/dialog';
import { CheckoutActions } from '@app/pages/checkout-app/checkout-store/actions';
import { AbtestDialogComponent } from '@app/pages/checkout-app/components/abtest/dialogs/abtest-dialog/abtest-dialog.component';
import { ACTIONS } from '@app/pages/checkout-app/components/abtest/models';
import { AngularAction } from './react/models/AngularAction';
import { BulkEditConditionDialogComponent } from '@app/pages/checkout-app/components/dialogs/bulk-edit-condition-dialog/bulk-edit-condition-dialog.component';
import { ToastService } from '@app/storybook/services/toast/toast.service';
import { NavigateAwayGuardService } from '@app/pages/checkout-app/shared/navigate-away-guard/navigate-away-guard.service';

@Component({
  selector: 'checkout-container',
  template: `@if (isLoading) {
      <div>
        <app-main-loader-lottie> </app-main-loader-lottie>
      </div>
    }
    <div #checkoutContainer></div>`,
  styleUrls: ['./checkout-container.component.scss'],
  encapsulation: ViewEncapsulation.None,
  standalone: false,
})
export class CheckoutContainerComponent implements OnInit, OnChanges, OnDestroy, AfterViewInit {
  @ViewChild('checkoutContainer', { static: false }) containerRef: ElementRef;
  root: ReactDOM.Root;
  angularRoutingSubject: Subject<string> = new Subject<string>();
  angularActionSubject: Subject<AngularAction> = new Subject<AngularAction>();
  destroyed$ = new Subject();
  isLoading = true;

  constructor(
    private apiService: ApiService,
    private shopService: ShopService,
    private router: Router,
    private store: Store,
    private dialog: MatDialog,
    private toastService: ToastService,
    private navigateAwayGuardService: NavigateAwayGuardService,
  ) {}

  ngOnInit(): void {
    this.angularRoutingSubject.pipe(takeUntil(this.destroyed$)).subscribe((url) => {
      if (url) {
        this.router.navigateByUrl(url);
      }
    });
    this.angularActionSubject.pipe(takeUntil(this.destroyed$)).subscribe((action) => {
      if (action.action === 'open-running-test-modal') {
        this.store.dispatch(CheckoutActions.setActiveShipping({ shippingId: action.payload.checkout.id }));
        this.dialog.open(AbtestDialogComponent, {
          data: { mode: ACTIONS.EDIT, testVariant: action.payload.checkout.runningTest, isPreviewOnly: true },
        });

        action.callback({});
      } else if (action.action === 'toast') {
        this.toastService.showToast(action.payload);

        action.callback({});
      } else if (action.action === 'show-hide-main-loader') {
        this.isLoading = action.payload.show;

        action.callback({});
      } else if (action.action === 'enable-navigate-away-guard') {
        this.navigateAwayGuardService.setCanNavigate(!action.payload.enabled);

        action.callback({});
      } else if (action.action === 'open-bulk-edit-condition-dialog') {
        const dialogRef = this.dialog.open(BulkEditConditionDialogComponent, {
          data: action.payload || {},
          panelClass: 'bulk-edit-condition-dialog-panel',
        });

        dialogRef.afterClosed().subscribe((result) => {
          action.callback(result);
        });
      } else if (action.action === 'refresh-ngrx-shippings') {
        try {
          this.store.dispatch(CheckoutActions.loadCheckouts());
        } finally {
          action.callback({});
        }
      } else {
        throw Error('Unknown action');
      }
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    this.render();
  }

  ngAfterViewInit() {
    this.render();
  }

  ngOnDestroy() {
    this.destroyed$.next(true);
    this.root.unmount();
  }

  private async render() {
    this.root = ReactDOM.createRoot(this.containerRef.nativeElement);
    this.root.render(
      <React.Suspense>
        <CheckoutRoot
          angularActionSubject={this.angularActionSubject}
          angularRoutingSubject={this.angularRoutingSubject}
          jwtToken={this.apiService.jwtToken}
          shop$={this.shopService.shop$}
        />
      </React.Suspense>,
    );
  }
}
